import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

// ====== API URLs ======
const BASE_URL = `${API_BASE_PATH}/applyleave`;

// ====== Types ======

// POST payload (apply leave)
export interface ApplyLeaveCreate {
  leave_type_id: number;
  start_date: string; // format: YYYY-MM-DD
  end_date?: string | null;
  duration_days: number;
  reason: string;
  notify: string;
}

// PUT payload (update leave request)
export interface ApplyLeaveUpdate {
  request_id: number;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason?: string | null;
  notify: string;
}

// API response object
export interface ApplyLeaveRecord {
  employee_id: string;
  leave_type_id: number;
  start_date: string;
  end_date: string | null;
  duration_days: number;
  reason: string | null;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason: string | null;
  lop_days: number;
  approver_id: string | null;
  created_at: string;
  updated_at: string | null;
  approved_date : string | null;
  request_id: number;
  notify: string;
}

// Widgets Response
export interface ApplyLeaveWidgets {
  applied: number;
  approved: number;
  rejected: number;
  pending: number;
  LOP: number;
  leave_count_name: {
    [leaveType: string]: number; // e.g. { "Casual Leave": 2, "Sick Leave": 0 }
  };
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ====== API Functions ======

// 🌟 Apply for Leave
export const postApplyLeave = async (
  data: ApplyLeaveCreate,
  token: string | null,
): Promise<ApplyLeaveRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.post(`${BASE_URL}/request`, formData.toString(), {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Leave applied successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Update Leave Request (Approve/Reject)
export const putApplyLeave = async (
  data: ApplyLeaveUpdate,
  token: string | null,
): Promise<ApplyLeaveRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "request_id" && value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.put(
      `${BASE_URL}/request/${data.request_id}`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    toast.success("Leave request updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Get All Leave Requests (Admin view)
export const getAllApplyLeave = async (
  token: string | null,
): Promise<ApplyLeaveRecord[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/get_all`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Get My Leave Requests (Employee view)
export const getSelfApplyLeave = async (
  token: string | null,
): Promise<ApplyLeaveRecord[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/self`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Get Leave Widgets (Stats per month/year)
export const getApplyLeaveWidgets = async (
  month: number,
  year: number,
  token?: string | null,
  day?: number | null,
  employee_id?: string | null,
): Promise<ApplyLeaveWidgets> => {
  try {
    const params = new URLSearchParams();
    params.append("month", month.toString());
    params.append("year", year.toString());
    if (day) params.append("day", day.toString());
    if (employee_id) params.append("employee_id", employee_id);

    const response = await axios.get(`${BASE_URL}/widgets?${params.toString()}`, {
      headers: {
        accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Update Leave Request
export const putUpdateLeave = async (
  requestId: number,
  data: Partial<ApplyLeaveRecord>, 
  token: string | null,
): Promise<ApplyLeaveRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.put(
      `${BASE_URL}/request/general/${requestId}`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Leave updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};